/**
 * Shared, client-safe Trends display-unit conversion + formatting.
 *
 * Trends stores observations in canonical units (kg, minutes, meters, °C). US practices display
 * imperial. This module owns that display transform so web (provider EHR) and mobile (patient app)
 * render identically — the conversion used to live only in the web components.
 *
 * Two distinct mechanisms:
 *   1. VALUE conversion (kg→lb, °C→°F clinic-only, m→mi): changes the numeric value, its reference
 *      range, statistics, every series point, and the unit label. Applied once per surface via
 *      `toImperialTrendData`. Display-only — stored observations are never mutated.
 *   2. DURATION formatting (sleep minutes → "7h 32m"): the value stays in minutes; only the rendered
 *      text changes. `formatMinutesAsDuration` / `getDisplayUnitLabel` are applied at render time.
 *
 * Blanket imperial for now; shaped so ENG-4727 (provider-selectable metric/US) can later parameterize
 * the target system. Keep this file pure — no server/`node`/`pg`/Medplum-client imports — so the
 * mobile Metro bundle stays clean (see packages/models/AGENTS.md).
 */

import { VitalLOINCCodes } from "../wearables/loinc";
import { CLINIC_PROVIDER_SLUG } from "./constants";
import type { ReferenceRange, TrendData, TrendStatistics } from "./types";

// -------------------------------------------------------------------------------------------------
//                                         UNIT LABELS
// -------------------------------------------------------------------------------------------------

const KILOGRAM_UNIT = "kg";
const POUND_UNIT = "lb";
const METER_UNIT = "m";
const MILE_UNIT = "mi";
const FAHRENHEIT_UNIT = "°F";
const MINUTES_UNIT = "min";

const BODY_TEMPERATURE_LOINC = VitalLOINCCodes.BodyTemperature.code;

// -------------------------------------------------------------------------------------------------
//                                      NUMERIC PRIMITIVES
// -------------------------------------------------------------------------------------------------

/** 1 mile = 1609.344 metres (exact). */
const METRES_PER_MILE = 1609.344;

export function roundTo(value: number, decimals: number): number {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

/** kg → lb (2 dp — matches the display default the web transform shipped with). */
export function kgToLb(value: number, decimals = 2): number {
  return roundTo(value * 2.2046226218, decimals);
}

/** lb → kg (4 dp — the inverse used when storing a pounds-edited goal back in canonical kg). */
export function lbToKg(value: number, decimals = 4): number {
  return roundTo(value * 0.45359237, decimals);
}

/**
 * Glucose mg/dL per mmol/L. Glucose molar mass is 180.156 g/mol (the same weight
 * `apps/web/lib/loinc/molecular-weight-registry.ts` gives UCUM), and 1 L = 10 dL.
 */
export const GLUCOSE_MG_DL_PER_MMOL_L = 18.016;

/**
 * Glucose mmol/L → mg/dL (2 dp). Glucose only — other analytes have a different molar mass
 * (cholesterol is ~38.67, not 18.016), so never apply this factor to a non-glucose value.
 */
export function glucoseMmolToMgDl(value: number, decimals = 2): number {
  return roundTo(value * GLUCOSE_MG_DL_PER_MMOL_L, decimals);
}

/** °C → °F (2 dp). */
export function cToF(value: number, decimals = 2): number {
  return roundTo((value * 9) / 5 + 32, decimals);
}

/** °F → °C (4 dp — inverse for goal storage). */
export function fToC(value: number, decimals = 4): number {
  return roundTo(((value - 32) * 5) / 9, decimals);
}

/** metres → miles (2 dp). */
export function metersToMiles(value: number, decimals = 2): number {
  return roundTo(value / METRES_PER_MILE, decimals);
}

/** miles → metres (4 dp — inverse for goal storage). */
export function milesToMeters(value: number, decimals = 4): number {
  return roundTo(value * METRES_PER_MILE, decimals);
}

// -------------------------------------------------------------------------------------------------
//                                     DURATION FORMATTING
// -------------------------------------------------------------------------------------------------

export function isDurationMinutesUnit(unit?: string): boolean {
  return unit === MINUTES_UNIT;
}

/** Formats a minute count as "7h 32m" / "45m" / "8h". */
export function formatMinutesAsDuration(totalMinutes: number): string {
  if (!Number.isFinite(totalMinutes)) {
    return "—";
  }

  const rounded = Math.round(totalMinutes);
  const abs = Math.abs(rounded);
  const hours = Math.floor(abs / 60);
  const minutes = abs % 60;

  let text = `${hours}h ${minutes}m`;
  if (hours === 0) {
    text = `${minutes}m`;
  } else if (minutes === 0) {
    text = `${hours}h`;
  }

  return rounded < 0 ? `-${text}` : text;
}

/**
 * The unit label to render beside a value. Empty for minute-durations, whose formatted value already
 * carries "h"/"m", so a trailing "min" would be redundant.
 */
export function getDisplayUnitLabel(unit?: string): string {
  if (isDurationMinutesUnit(unit)) {
    return "";
  }

  return unit ?? "";
}

// -------------------------------------------------------------------------------------------------
//                                    SOURCE NAME SANITIZING
// -------------------------------------------------------------------------------------------------

// Units that lab-parsed / migrated Observations sometimes bake into a free-text name
// (e.g. code.text "Body Weight (Kilogram)"). Trends renders the unit separately and may
// convert it (kg→lb), so a source-derived name carrying its own unit suffix is both
// redundant and wrong post-conversion. Matched case-insensitively against the trailing
// parenthetical's inner text — only recognized unit tokens are stripped, so qualifiers
// like "(Total)", "(25-OH)", or "(Fasting)" are left intact.
const REDUNDANT_UNIT_LABELS = new Set<string>([
  "kilogram",
  "kilograms",
  "kg",
  "gram",
  "grams",
  "g",
  "milligram",
  "milligrams",
  "mg",
  "microgram",
  "micrograms",
  "mcg",
  "µg",
  "pound",
  "pounds",
  "lb",
  "lbs",
  "ounce",
  "ounces",
  "oz",
  "meter",
  "meters",
  "metre",
  "metres",
  "m",
  "centimeter",
  "centimeters",
  "centimetre",
  "centimetres",
  "cm",
  "millimeter",
  "millimeters",
  "mm",
  "inch",
  "inches",
  "in",
  "foot",
  "feet",
  "ft",
  "celsius",
  "fahrenheit",
  "degrees celsius",
  "degrees fahrenheit",
  "°c",
  "°f",
  "percent",
  "%",
  "minute",
  "minutes",
  "min",
  "hour",
  "hours",
  "second",
  "seconds",
  "beats per minute",
  "bpm",
]);

const TRAILING_PARENTHETICAL = /^(.*\S)\s*\(([^()]+)\)\s*$/;

/**
 * Drops a redundant trailing unit suffix from a source-derived biomarker name
 * ("Body Weight (Kilogram)" → "Body Weight"). Only strips when the parenthetical is a
 * recognized unit token; anything else (clinical qualifiers, method notes) is preserved.
 * Curated config/catalog names never carry these suffixes, so this only touches raw
 * source text that leaked through as a display name.
 */
export function stripRedundantUnitSuffix(name: string): string {
  const match = name.match(TRAILING_PARENTHETICAL);
  if (!match) {
    return name;
  }

  const [, base, inner] = match;
  if (REDUNDANT_UNIT_LABELS.has(inner.trim().toLocaleLowerCase())) {
    return base;
  }

  return name;
}

// -------------------------------------------------------------------------------------------------
//                                   VALUE CONVERSION (TrendData)
// -------------------------------------------------------------------------------------------------

type NumericTransform = (value: number) => number;

function convertRange(
  range: ReferenceRange | undefined,
  convert: NumericTransform,
): ReferenceRange | undefined {
  if (!range) return range;

  return {
    normal: { min: convert(range.normal.min), max: convert(range.normal.max) },
    optimal: { min: convert(range.optimal.min), max: convert(range.optimal.max) },
  };
}

function convertStatistics(
  statistics: TrendStatistics,
  convert: NumericTransform,
): TrendStatistics {
  return {
    ...statistics,
    current: convert(statistics.current),
    average: convert(statistics.average),
    min: convert(statistics.min),
    max: convert(statistics.max),
    rolling30DayAvg:
      statistics.rolling30DayAvg == null ? undefined : convert(statistics.rolling30DayAvg),
    previousValues: statistics.previousValues?.map((entry) => ({
      ...entry,
      value: convert(entry.value),
    })),
  };
}

/**
 * Applies a numeric transform across every value-bearing field of a TrendData (series, per-source
 * series, statistics, reference range, goal range) and stamps the display unit. Display-only.
 */
function convertTrendDataValues(
  data: TrendData,
  convert: NumericTransform,
  displayUnit: string,
): TrendData {
  return {
    ...data,
    observations: data.observations.map((observation) => ({
      ...observation,
      value: convert(observation.value),
    })),
    bySource: data.bySource
      ? Object.fromEntries(
          Object.entries(data.bySource).map(([slug, observations]) => [
            slug,
            observations.map((observation) => ({
              ...observation,
              value: convert(observation.value),
            })),
          ]),
        )
      : undefined,
    statistics: convertStatistics(data.statistics, convert),
    referenceRange: convertRange(data.referenceRange, convert),
    goalRange: convertRange(data.goalRange, convert),
    metadata: { ...data.metadata, unit: displayUnit },
  };
}

/**
 * Treat legacy observations without a provider slug as clinic data so old temperature entries still
 * follow the clinic-only Fahrenheit display rule.
 */
function isClinicObservation(provider?: string): boolean {
  return provider == null || provider === CLINIC_PROVIDER_SLUG;
}

/**
 * Only convert clinic temperature when the entire dataset is clinic-only. Mixed-source datasets stay
 * in their stored unit to avoid partial conversions.
 */
function isAllClinicTemperatureDataset(data: TrendData): boolean {
  const hasOnlyClinicObservations = data.observations.every((observation) =>
    isClinicObservation(observation.provider),
  );
  if (!hasOnlyClinicObservations) {
    return false;
  }

  return Object.entries(data.bySource ?? {}).every(([slug, observations]) => {
    if (slug !== CLINIC_PROVIDER_SLUG) {
      return observations.length === 0;
    }
    return observations.every((observation) => isClinicObservation(observation.provider));
  });
}

export interface ImperialTrendDataArgs {
  /** The biomarker's LOINC code — used only to gate the clinic body-temperature rule. */
  biomarkerId: string;
  /** The provider slug of the source being viewed; the temp rule only fires for `clinic`. */
  providerSlug?: string;
  data: TrendData | null | undefined;
}

/**
 * Converts a biomarker's TrendData to its imperial display form (value + range + statistics + every
 * series point + unit label). Returns the input unchanged when no rule applies (or `undefined` for
 * empty input). Mirrors the web transform and adds the metres→miles branch.
 *
 * Rules (mutually exclusive, in order):
 *   - Body temperature from a clinic-only dataset → °F.
 *   - Any `kg` biomarker → lb (body weight + body-composition metrics).
 *   - Any `m` biomarker → miles.
 * Duration (`min`) is intentionally NOT converted here — see `formatMinutesAsDuration`.
 */
export function toImperialTrendData({
  biomarkerId,
  providerSlug,
  data,
}: ImperialTrendDataArgs): TrendData | undefined {
  if (!data) {
    return undefined;
  }

  // Unit-keyed display conversion needs the unit plus the value-bearing statistics to transform. A
  // degraded or partial response (or a test fixture) may lack them — pass through unconverted rather
  // than throw and blank the screen. `metadata`/`statistics` are required by the type, so real
  // producers always set them; this only guards the malformed/degraded edge.
  if (!data.metadata?.unit || !data.statistics) {
    return data;
  }

  const isClinicBodyTemperature =
    biomarkerId === BODY_TEMPERATURE_LOINC && providerSlug === CLINIC_PROVIDER_SLUG;
  if (isClinicBodyTemperature) {
    if (!isAllClinicTemperatureDataset(data)) {
      return data;
    }
    return convertTrendDataValues(data, (value) => cToF(value), FAHRENHEIT_UNIT);
  }

  if (data.metadata.unit === KILOGRAM_UNIT) {
    return convertTrendDataValues(data, (value) => kgToLb(value), POUND_UNIT);
  }

  if (data.metadata.unit === METER_UNIT) {
    return convertTrendDataValues(data, (value) => metersToMiles(value), MILE_UNIT);
  }

  return data;
}

/**
 * Reverses a display-unit LABEL back to its canonical stored form (lb→kg, mi→m) — the label-only
 * inverse of `toImperialTrendData`. For callers that imperialize TrendData for display but still need
 * the canonical unit to reason about the stored observations behind it. The motivating case is Trends
 * biomarker merge: candidates are built from imperialized rows, but merge matches each candidate
 * against raw canonical (kg/m) observations by unit — a `lb`/`mi` candidate matches nothing and the
 * merge hard-errors. Restoring the canonical label keeps merge scoping correct.
 *
 * Only the two units `imperializeTrendDataRecord` (no `providerSlug`) actually converts are reversed;
 * every other unit — labs (`mg/dL`, `mmol/L`), `kg/m²`, durations — passes through unchanged. Clinic
 * body-temperature (°F) is deliberately not reversed: it converts only when a `providerSlug` is
 * threaded, which the merge-candidate build path never does, so those units are already canonical.
 */
export function toCanonicalUnitLabel(unit: string): string {
  if (unit === POUND_UNIT) {
    return KILOGRAM_UNIT;
  }
  if (unit === MILE_UNIT) {
    return METER_UNIT;
  }
  return unit;
}

// -------------------------------------------------------------------------------------------------
//                                    GOAL RANGE ROUND-TRIP
// -------------------------------------------------------------------------------------------------

interface GoalRange {
  min: number;
  max: number;
}

export interface GoalRangeArgs {
  /**
   * The unit the goal was edited in (i.e. the displayed `metadata.unit`). Both directions key on this
   * so a goal always round-trips through the same rule that rendered it.
   */
  displayUnit?: string;
  goalRange: GoalRange;
}

/** Inverts a goal edited in the displayed unit back to the canonical stored unit (°F→°C, lb→kg, mi→m). */
export function toStoredGoalRange({ displayUnit, goalRange }: GoalRangeArgs): GoalRange {
  if (displayUnit === FAHRENHEIT_UNIT) {
    return { min: fToC(goalRange.min), max: fToC(goalRange.max) };
  }
  if (displayUnit === POUND_UNIT) {
    return { min: lbToKg(goalRange.min), max: lbToKg(goalRange.max) };
  }
  if (displayUnit === MILE_UNIT) {
    return { min: milesToMeters(goalRange.min), max: milesToMeters(goalRange.max) };
  }
  return goalRange;
}

/** Converts a stored goal (canonical units) into the displayed unit — inverse of `toStoredGoalRange`. */
export function toDisplayGoalRange({ displayUnit, goalRange }: GoalRangeArgs): GoalRange {
  if (displayUnit === FAHRENHEIT_UNIT) {
    return { min: cToF(goalRange.min), max: cToF(goalRange.max) };
  }
  if (displayUnit === POUND_UNIT) {
    return { min: kgToLb(goalRange.min), max: kgToLb(goalRange.max) };
  }
  if (displayUnit === MILE_UNIT) {
    return { min: metersToMiles(goalRange.min), max: metersToMiles(goalRange.max) };
  }
  return goalRange;
}
